import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate, utcMs } from '../utils/dateFormat';
import type { ExpenseClaimDto, ExpenseClaimDocumentDto } from '../types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import { useExpenseVatOptions } from '../hooks/useExpenseVatOptions';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { ExchangeRateBanner } from '../components/ExchangeRateBanner';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotApprove, couldNotReject, documentCopy } from '../utils/userFacingCopy';

const STATUS_COLOUR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  Draft: 'default',
  PendingManagerApproval: 'warning',
  Approved: 'success',
  Rejected: 'error',
  Withdrawn: 'info',
  PendingFinanceApproval: 'warning',
};

const ALL_STATUSES_VALUE = 'ALL';

const TEAM_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: ALL_STATUSES_VALUE, label: 'All statuses' },
  { value: 'PendingManagerApproval', label: 'Pending manager approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'PendingFinanceApproval', label: 'Pending finance approval' },
];

export default function TeamExpensesPage() {
  const navigate = useNavigate();
  const { getCurrencySymbol, getCurrencyCode } = useExpenseVatOptions();
  const { rates: exchangeRates, convertToGbp } = useExchangeRates();
  const [claims, setClaims] = useState<ExpenseClaimDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('PendingManagerApproval');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [approveLoadingId, setApproveLoadingId] = useState<string | null>(null);
  const [approveDialogClaim, setApproveDialogClaim] = useState<ExpenseClaimDto | null>(null);
  const [rejectDialogClaim, setRejectDialogClaim] = useState<ExpenseClaimDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [documentsByClaimId, setDocumentsByClaimId] = useState<Record<string, ExpenseClaimDocumentDto[]>>({});
  const [documentsLoadingId, setDocumentsLoadingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'requester' | 'claimDate' | 'payee' | 'totalGbp' | 'status' | 'submitted'>('claimDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const summaryByStatus = useMemo(() => {
    const pendingApproval = claims.filter((c) => c.status === 'PendingManagerApproval');
    const approved = claims.filter((c) => c.status === 'Approved');
    const pendingFinance = claims.filter((c) => c.status === 'PendingFinanceApproval');
    const toGbp = (list: ExpenseClaimDto[]) =>
      list.reduce((sum, c) => sum + convertToGbp(c.total, getCurrencyCode(c.vatRate)), 0);
    return {
      pendingApproval: { count: pendingApproval.length, totalGbp: toGbp(pendingApproval) },
      approved: { count: approved.length, totalGbp: toGbp(approved) },
      pendingFinance: { count: pendingFinance.length, totalGbp: toGbp(pendingFinance) },
    };
  }, [claims, convertToGbp, getCurrencyCode]);

  const filteredClaims = useMemo(() => {
    let result = claims;
    if (statusFilter && statusFilter !== ALL_STATUSES_VALUE) {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.requesterName ?? '').toLowerCase().includes(q) ||
          c.payee.toLowerCase().includes(q) ||
          formatDate(c.claimDate).toLowerCase().includes(q) ||
          c.total.toFixed(2).includes(q)
      );
    }
    return result;
  }, [claims, searchQuery, statusFilter]);

  const handleSort = (field: 'requester' | 'claimDate' | 'payee' | 'totalGbp' | 'status' | 'submitted') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'requester' | 'claimDate' | 'payee' | 'totalGbp' | 'status' | 'submitted') => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const sortedClaims = useMemo(() => {
    return [...filteredClaims].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortField === 'requester') {
        aVal = (a.requesterName ?? '').toLowerCase();
        bVal = (b.requesterName ?? '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'claimDate') {
        aVal = utcMs(a.claimDate);
        bVal = utcMs(b.claimDate);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortField === 'payee') {
        aVal = a.payee.toLowerCase();
        bVal = b.payee.toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'totalGbp') {
        aVal = convertToGbp(a.total, getCurrencyCode(a.vatRate));
        bVal = convertToGbp(b.total, getCurrencyCode(b.vatRate));
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }
      if (sortField === 'status') {
        aVal = (a.status ?? '').toLowerCase();
        bVal = (b.status ?? '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = utcMs(a.submittedAtUtc);
      bVal = utcMs(b.submittedAtUtc);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredClaims, sortField, sortDirection, convertToGbp, getCurrencyCode]);

  const paginatedClaims = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedClaims.slice(start, start + rowsPerPage);
  }, [sortedClaims, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);

  const loadTeamClaims = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get<ExpenseClaimDto[]>(API_ENDPOINTS.EXPENSE_CLAIMS.TEAM);
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('team expense claims')));
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async (claimId: string) => {
    setDocumentsLoadingId(claimId);
    try {
      const res = await apiService.get<ExpenseClaimDocumentDto[]>(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENTS(claimId));
      setDocumentsByClaimId((prev) => ({ ...prev, [claimId]: Array.isArray(res.data) ? res.data : [] }));
    } catch {
      setDocumentsByClaimId((prev) => ({ ...prev, [claimId]: [] }));
    } finally {
      setDocumentsLoadingId(null);
    }
  }, []);

  useEffect(() => {
    loadTeamClaims();
  }, [loadTeamClaims]);

  useEffect(() => {
    if (expandedId) loadDocuments(expandedId);
  }, [expandedId, loadDocuments]);

  const handleApprove = async (claimId: string) => {
    setApproveLoadingId(claimId);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.EXPENSE_CLAIMS.APPROVE(claimId));
      await loadTeamClaims();
      if (expandedId === claimId) setExpandedId(null);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotApprove('the claim')));
    } finally {
      setApproveLoadingId(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectDialogClaim) return;
    setRejectSubmitting(true);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.EXPENSE_CLAIMS.REJECT(rejectDialogClaim.id), {
        rejectedReason: rejectReason.trim() || undefined,
      });
      await loadTeamClaims();
      setRejectDialogClaim(null);
      setRejectReason('');
      if (expandedId === rejectDialogClaim.id) setExpandedId(null);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotReject('the claim')));
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleDownloadDocument = async (claimId: string, documentId: string) => {
    try {
      const res = await apiService.get<Blob>(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENT_DOWNLOAD(claimId, documentId), {
        responseType: 'blob',
      });
      const docs = documentsByClaimId[claimId] ?? [];
      const doc = docs.find((d) => d.id === documentId);
      const fileName = doc?.fileName ?? 'receipt';
      const contentType = doc?.contentType ?? '';
      const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      setError(documentCopy.download);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Team Expenses
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            View all expense claims from your team. Use the status filter to see pending approvals, approved, rejected, or all. Approve or reject claims that are pending your approval.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <ExchangeRateBanner rates={exchangeRates} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Card
            variant="outlined"
            sx={{
              minWidth: 180,
              flex: 1,
              cursor: 'pointer',
              borderColor: statusFilter === 'PendingManagerApproval' ? 'primary.main' : 'divider',
              borderWidth: statusFilter === 'PendingManagerApproval' ? 2 : 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setStatusFilter('PendingManagerApproval')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PendingActionsIcon color="warning" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Pending my approval</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {summaryByStatus.pendingApproval.count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                £{summaryByStatus.pendingApproval.totalGbp.toFixed(2)} total
              </Typography>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              minWidth: 180,
              flex: 1,
              cursor: 'pointer',
              borderColor: statusFilter === 'Approved' ? 'success.main' : 'divider',
              borderWidth: statusFilter === 'Approved' ? 2 : 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setStatusFilter('Approved')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Approved</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {summaryByStatus.approved.count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                £{summaryByStatus.approved.totalGbp.toFixed(2)} total
              </Typography>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{
              minWidth: 180,
              flex: 1,
              cursor: 'pointer',
              borderColor: statusFilter === 'PendingFinanceApproval' ? 'info.main' : 'divider',
              borderWidth: statusFilter === 'PendingFinanceApproval' ? 2 : 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => setStatusFilter('PendingFinanceApproval')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AccountBalanceIcon color="info" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Pending finance approval</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {summaryByStatus.pendingFinance.count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                £{summaryByStatus.pendingFinance.totalGbp.toFixed(2)} total
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <TextField
                sx={{ minWidth: 280, flex: 1 }}
                label="Search team claims"
                placeholder="Search by requester, payee, date, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel id="team-expenses-status-label">Status</InputLabel>
                <Select
                  labelId="team-expenses-status-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {TEAM_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchQuery.trim() !== '' || (statusFilter && statusFilter !== ALL_STATUSES_VALUE)) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => setSearchQuery('')}
                    size="small"
                    color="primary"
                  />
                )}
                {statusFilter && statusFilter !== ALL_STATUSES_VALUE && (
                  <Chip
                    label={`Status: ${TEAM_STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? statusFilter}`}
                    onDelete={() => setStatusFilter(ALL_STATUSES_VALUE)}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {filteredClaims.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {(searchQuery || (statusFilter && statusFilter !== ALL_STATUSES_VALUE)) ? 'No team claims match your search or filter.' : 'No team expense claims yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(searchQuery || (statusFilter && statusFilter !== ALL_STATUSES_VALUE)) ? 'Try adjusting your search or status filter.' : 'Expense claims from your direct reports will appear here.'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} />
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('requester')}>
                            <strong>Requester</strong>
                            {getSortIcon('requester')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('claimDate')}>
                            <strong>Claim date</strong>
                            {getSortIcon('claimDate')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('payee')}>
                            <strong>Payee</strong>
                            {getSortIcon('payee')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('totalGbp')}>
                            <strong>Total (GBP)</strong>
                            {getSortIcon('totalGbp')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                            <strong>Status</strong>
                            {getSortIcon('status')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('submitted')}>
                            <strong>Submitted</strong>
                            {getSortIcon('submitted')}
                          </Box>
                        </TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedClaims.map((claim) => (
                      <React.Fragment key={claim.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedId(expandedId === claim.id ? null : claim.id)}
                              aria-label={expandedId === claim.id ? 'Collapse' : 'Expand'}
                            >
                              {expandedId === claim.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{claim.requesterName ?? 'Unknown'}</TableCell>
                          <TableCell>{formatDate(claim.claimDate)}</TableCell>
                          <TableCell>{claim.payee}</TableCell>
                          <TableCell align="right">{getCurrencySymbol(claim.vatRate)}{claim.total.toFixed(2)}</TableCell>
                          <TableCell align="right">£{convertToGbp(claim.total, getCurrencyCode(claim.vatRate)).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={claim.status ?? '—'} color={STATUS_COLOUR[claim.status ?? ''] ?? 'default'} />
                          </TableCell>
                          <TableCell>{formatDate(claim.submittedAtUtc, { includeTime: true })}</TableCell>
                          <TableCell align="right">
                            {claim.status === 'PendingManagerApproval' ? (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => setApproveDialogClaim(claim)}
                                  disabled={approveLoadingId === claim.id}
                                  sx={{ textTransform: 'none', mr: 1 }}
                                >
                                  {approveLoadingId === claim.id ? <CircularProgress size={16} /> : 'Approve'}
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<CancelIcon />}
                                  onClick={() => setRejectDialogClaim(claim)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none' }}>
                            <Collapse in={expandedId === claim.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, pl: 2 }}>
                                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                                  Claim details
                                </Typography>
                                <Typography variant="body2">Payee: {claim.payee}</Typography>
                                <Typography variant="body2">VAT: {claim.vatRate}% · Subtotal: {getCurrencySymbol(claim.vatRate)}{claim.subtotal.toFixed(2)} · VAT: {getCurrencySymbol(claim.vatRate)}{claim.vatAmount.toFixed(2)} · Total: {getCurrencySymbol(claim.vatRate)}{claim.total.toFixed(2)}</Typography>
                                {claim.notes && <Typography variant="body2" sx={{ mt: 1 }}>Notes: {claim.notes}</Typography>}
                                {claim.lineItems?.length ? (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="600">Line items</Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Description</TableCell>
                                          <TableCell>Category</TableCell>
                                          <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {claim.lineItems.map((li) => (
                                          <TableRow key={li.id}>
                                            <TableCell>{li.description}</TableCell>
                                            <TableCell>{li.expenseCategoryName ?? li.expenseCategoryGlCode ?? '—'}</TableCell>
                                            <TableCell align="right">{getCurrencySymbol(claim.vatRate)}{li.amount.toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                ) : null}
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" fontWeight="600">
                                    <ReceiptIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Receipts
                                  </Typography>
                                  {documentsLoadingId === claim.id ? (
                                    <CircularProgress size={20} />
                                  ) : (documentsByClaimId[claim.id] ?? []).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No receipts attached.</Typography>
                                  ) : (
                                    <List dense>
                                      {(documentsByClaimId[claim.id] ?? []).map((doc) => (
                                        <ListItem key={doc.id}>
                                          <ListItemText primary={doc.fileName} />
                                          <ListItemSecondaryAction>
                                            <IconButton size="small" onClick={() => handleDownloadDocument(claim.id, doc.id)} aria-label="Download">
                                              <DownloadIcon />
                                            </IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedClaims.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={!!approveDialogClaim} onClose={() => setApproveDialogClaim(null)}>
        <DialogTitle>Approve expense claim?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {approveDialogClaim && (
              <>
                You are about to approve the claim from <strong>{approveDialogClaim.requesterName ?? 'Unknown'}</strong>{' '}
                for <strong>{getCurrencySymbol(approveDialogClaim.vatRate)}{approveDialogClaim.total.toFixed(2)}</strong> (payee: {approveDialogClaim.payee}). This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogClaim(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!approveDialogClaim) return;
              const claimId = approveDialogClaim.id;
              setApproveDialogClaim(null);
              await handleApprove(claimId);
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectDialogClaim} onClose={() => !rejectSubmitting && setRejectDialogClaim(null)}>
        <DialogTitle>Reject expense claim</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Optionally provide a reason for rejection (visible to the requester). You can leave it blank.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for rejection"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejectSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogClaim(null)} disabled={rejectSubmitting}>Cancel</Button>
          <Button onClick={handleRejectSubmit} color="error" variant="contained" disabled={rejectSubmitting}>
            {rejectSubmitting ? <CircularProgress size={20} /> : 'Reject claim'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
